import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, MessageSquareText, Phone, Plus, Trash2 } from 'lucide-react';

import { adminFetch } from '@/hooks/admin/useAdminApi';
import { Button } from '@/components/ui/button';
import { CollapsibleCard } from '@/components/admin/CollapsibleCard';
import { Input } from '@/components/ui/input';

/**
 * Admin → customer detail → Billing & comms: the consolidated phone directory
 * (orders.customer_phones). These are the numbers the Chloe phone line
 * recognizes callers by. Pull buttons import from QuickBooks (parent + every
 * sub-location); manual adds cover anything
 * else. Text-consent is shown read-only here — the customer grants it in
 * their own Settings so the opt-in record stays genuine.
 */

interface PhoneRow {
  id: string;
  phone: string;
  phone_digits: string | null;
  label: string | null;
  source: string;
  sms_consent_at: string | null;
  sms_opt_out_at: string | null;
}

const SOURCE_BADGE: Record<string, string> = {
  qbo: 'QuickBooks',
  billandpay: 'Bill & Pay',
  portal_user: 'Portal user',
  manual: 'Manual',
  apply: 'Application',
};

function fmt(p: PhoneRow): string {
  const d = p.phone_digits ?? p.phone.replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p.phone;
}

export default function PhoneDirectoryPanel({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const key = ['admin-customer-phones', customerId] as const;

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () =>
      adminFetch<{ ok: true; phones: PhoneRow[] }>(
        `admin-customer-phones?customer=${customerId}`,
      ),
  });

  const act = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      adminFetch<{ ok: true; added?: number }>('admin-customer-phones', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, ...body }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });

  const [newPhone, setNewPhone] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const run = async (body: Record<string, unknown>, okMsg: (r: { added?: number }) => string) => {
    setNotice(null);
    try {
      const r = await act.mutateAsync(body);
      setNotice(okMsg(r));
    } catch (err) {
      setNotice(err instanceof Error ? err.message : String(err));
    }
  };

  const phones = data?.phones ?? [];

  return (
    <CollapsibleCard
      title="Phone directory"
      icon={<Phone size={14} className="shrink-0" />}
      storageKey={`phones-${customerId}`}
      defaultOpen={false}
      summary={phones.length === 0 ? 'No numbers' : `${phones.length} number${phones.length === 1 ? '' : 's'}`}
      desc={
        <>
          Callers from these numbers are recognized automatically on our phone line.
          Text-message opt-in is granted by the customer in their portal Settings.
        </>
      }
      action={
        <Button
          size="sm"
          variant="secondary"
          disabled={act.isPending}
          onClick={() =>
            void run({ action: 'sync_qbo' }, (r) => `QuickBooks pull done — ${r.added ?? 0} new number(s).`)
          }
        >
          <Download size={12} /> Pull from QuickBooks
        </Button>
      }
    >

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 size={13} className="animate-spin" /> Loading…
        </div>
      ) : phones.length === 0 ? (
        <p className="text-sm tracking-tight text-slate-500">No numbers yet — pull or add one.</p>
      ) : (
        <ul className="divide-y divide-brix-border rounded-lg border border-brix-border">
          {phones.map((p) => {
            const smsOn = p.sms_consent_at != null && p.sms_opt_out_at == null;
            return (
              <li key={p.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                <span className="font-semibold tracking-tight text-slate-900">{fmt(p)}</span>
                {p.label && <span className="text-slate-600">{p.label}</span>}
                <span className="rounded-full border border-brix-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">
                  {SOURCE_BADGE[p.source] ?? p.source}
                </span>
                {smsOn && (
                  <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                    <MessageSquareText size={10} /> Texts OK
                  </span>
                )}
                <span className="flex-1" />
                <button
                  type="button"
                  className="text-slate-400 hover:text-red-600"
                  title="Remove"
                  onClick={() => void run({ action: 'remove', id: p.id }, () => 'Removed.')}
                >
                  <Trash2 size={13} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!newPhone.trim()) return;
          void run({ action: 'add', phone: newPhone.trim(), label: newLabel.trim() || undefined }, () => {
            setNewPhone('');
            setNewLabel('');
            return 'Number added.';
          });
        }}
      >
        <Input
          type="tel"
          value={newPhone}
          onChange={(e) => setNewPhone(e.target.value)}
          placeholder="(510) 555-1234"
          className="h-8 w-44"
        />
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="Label"
          className="h-8 w-40"
        />
        <Button type="submit" size="sm" variant="secondary" disabled={act.isPending}>
          {act.isPending ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add
        </Button>
        {notice && <span className="text-xs tracking-tight text-slate-600">{notice}</span>}
      </form>
    </CollapsibleCard>
  );
}
